import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Download } from 'lucide-react';
import { formatHours } from '../../../utils/helpers';
import { addWatermarkToPdf } from '../../../utils/pdfWatermark';

interface DownloadPDFProps {
    period: 'week' | 'month' | 'year';
    project?: { name: string };
    currentData: {
        totalHours: number;
        averageSession: number;
        data: any[];
    };
    totalSessions: number;
    isLoading?: boolean;
    onSuccess?: () => void;
    onError?: (error: any) => void;
    /** Optional date range for the report */
    dateFrom?: string;
    dateTo?: string;
}

const DownloadPDF: React.FC<DownloadPDFProps> = ({
    period,
    project,
    currentData,
    totalSessions,
    isLoading = false,
    onSuccess,
    onError,
    dateFrom,
    dateTo,
}) => {
    const handleDownload = async () => {
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const dateStr = new Date().toLocaleDateString();
            const periodLabel = period.charAt(0).toUpperCase() + period.slice(1);
            const projectName = project ? project.name : 'All Projects';
            const hasDateRange = dateFrom && dateTo;
            const dateRangeLabel = hasDateRange
                ? `${new Date(dateFrom).toLocaleDateString()} – ${new Date(dateTo).toLocaleDateString()}`
                : null;

            // Brand Colors - Using a more sophisticated palette
            const brandColor: [number, number, number] = [234, 88, 12]; // Orange-600 (Primary)
            const brandDark: [number, number, number] = [124, 45, 18]; // Orange-900 (Text)
            const brandLight: [number, number, number] = [255, 247, 237]; // Orange-50 (Backgrounds)

            const slate900: [number, number, number] = [15, 23, 42]; // Main Text
            const slate500: [number, number, number] = [100, 116, 139]; // Muted Text
            const slate100: [number, number, number] = [241, 245, 249]; // Card Bg relative

            // Icon Drawing Helper (Vector paths for crisp rendering)
            const drawIcon = (type: 'clock' | 'list' | 'timer' | 'chart', x: number, y: number, color: [number, number, number]) => {
                doc.setDrawColor(color[0], color[1], color[2]);
                doc.setLineWidth(1);
                doc.setFillColor(color[0], color[1], color[2]); // For fills if needed

                switch (type) {
                    case 'clock':
                        doc.circle(x + 5, y + 5, 4); // Face
                        doc.line(x + 5, y + 5, x + 5, y + 3); // Hour hand
                        doc.line(x + 5, y + 5, x + 7, y + 5); // Minute hand
                        break;
                    case 'list':
                        doc.rect(x + 2, y + 2, 6, 6); // Box
                        doc.line(x + 3, y + 4, x + 7, y + 4);
                        doc.line(x + 3, y + 6, x + 7, y + 6);
                        break;
                    case 'timer':
                        doc.circle(x + 5, y + 5, 3.5);
                        doc.line(x + 5, y + 1.5, x + 5, y + 0.5); // Top button
                        doc.line(x + 5, y + 5, x + 6.5, y + 6.5); // Hand
                        break;
                    case 'chart':
                        doc.line(x + 2, y + 8, x + 2, y + 2); // Y axis
                        doc.line(x + 2, y + 8, x + 8, y + 8); // X axis
                        doc.line(x + 2, y + 8, x + 4, y + 5);
                        doc.line(x + 4, y + 5, x + 6, y + 6);
                        doc.line(x + 6, y + 6, x + 8, y + 2); // Trend line
                        break;
                }
            };

            // Helper to add Header and Footer to every page
            const addPageOverlay = (data: any) => {
                const doc = data.doc;
                const pageNumber = data.pageNumber;

                // --- Header ---
                // Modern "Hero" background for top area
                const headerHeight = 40;
                doc.setFillColor(brandLight[0], brandLight[1], brandLight[2]);
                doc.rect(0, 0, pageWidth, headerHeight, 'F');

                // Accent Line (Full Width)
                doc.setDrawColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.setLineWidth(1.5);
                doc.line(0, headerHeight, pageWidth, headerHeight);

                // Logo / Brand Name (Vertically Centered approx y=20-25)
                doc.setFontSize(24);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.text('Focuzon', 14, 22);

                // Report Title (Aligned next to logo, smaller)
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                doc.text('ANALYTICS REPORT', 14, 30);

                // Right side header info (Capsules)
                const capsuleHeight = 7;
                const capsuleGap = 4;
                const rightMargin = 14;

                // Helper to draw a capsule with label and text
                const drawCapsule = (text: string, y: number) => {
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    const textWidth = doc.getTextWidth(text);
                    const paddingX = 4;
                    const boxWidth = textWidth + (paddingX * 2);
                    const x = pageWidth - rightMargin - boxWidth;

                    // Box
                    doc.setFillColor(255, 255, 255);
                    doc.setDrawColor(brandColor[0], brandColor[1], brandColor[2]);
                    doc.setLineWidth(0.1);
                    doc.roundedRect(x, y, boxWidth, capsuleHeight, 3.5, 3.5, 'FD');

                    // Text
                    doc.setTextColor(slate900[0], slate900[1], slate900[2]);
                    doc.text(text, x + paddingX, y + 4.5);
                    return x; // Return left position for potential chaining if needed
                };

                // Date Capsule
                drawCapsule(`Generated: ${dateStr}`, 10);

                // Project Capsule
                let safeProjectName = projectName;
                if (safeProjectName.length > 25) {
                    safeProjectName = safeProjectName.substring(0, 22) + '...';
                }
                drawCapsule(`Project: ${safeProjectName}`, 19);

                // Type Capsule
                drawCapsule(`Type: ${periodLabel}ly Analysis`, 28);

                // Date range capsule when selected
                if (dateRangeLabel) {
                    const rangeText = `Range: ${dateRangeLabel}`;
                    const rangeWidth = Math.min(doc.getTextWidth(rangeText) + 8, pageWidth / 2 - 20);
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    const y = 37;
                    doc.setFillColor(255, 255, 255);
                    doc.setDrawColor(brandColor[0], brandColor[1], brandColor[2]);
                    doc.setLineWidth(0.1);
                    doc.roundedRect(pageWidth - rightMargin - rangeWidth, y, rangeWidth, capsuleHeight, 3.5, 3.5, 'FD');
                    doc.setTextColor(slate900[0], slate900[1], slate900[2]);
                    doc.text(rangeText.length > 28 ? rangeText.substring(0, 25) + '...' : rangeText, pageWidth - rightMargin - rangeWidth + 4, y + 4.5);
                }

                // --- Footer ---
                doc.setDrawColor(226, 232, 240); // Slate-200
                doc.setLineWidth(0.5);
                doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);

                doc.setFontSize(8);
                doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                doc.text(`© ${new Date().getFullYear()} Focuzon. All rights reserved.`, 14, pageHeight - 8);

                const footerCredit = 'www.focuzon.com';
                doc.text(footerCredit, pageWidth / 2, pageHeight - 8, { align: 'center' });

                doc.text(`Page ${pageNumber}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
            };

            // 1. Draw Summary Cards (Only on first page)
            const drawSummary = () => {
                const startY = 50;
                const cardGap = 12;
                const cardWidth = (pageWidth - 28 - (cardGap * 2)) / 3;

                const summaries = [
                    { label: 'TOTAL HOURS', value: formatHours(currentData.totalHours), icon: 'clock' as const },
                    { label: 'TOTAL SESSIONS', value: totalSessions.toString(), icon: 'list' as const },
                    { label: 'AVG DURATION', value: formatHours(currentData.averageSession), icon: 'timer' as const },
                ];



                summaries.forEach((stat, index) => {
                    const x = 14 + (index * (cardWidth + cardGap));
                    const y = startY;

                    // Icon Circle Background (Keep this for structure)
                    doc.setFillColor(brandLight[0], brandLight[1], brandLight[2]);
                    doc.circle(x + 10, y + 10, 7, 'F');

                    // Draw Icon
                    drawIcon(stat.icon, x + 5, y + 5, brandColor);

                    // Label (Title) - To the right of icon
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                    doc.text(stat.label, x + 20, y + 9);

                    // Value - Under the title, smaller font size
                    const valueFontSize = 12; // Reduced from 14
                    doc.setFontSize(valueFontSize);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(slate900[0], slate900[1], slate900[2]);
                    doc.text(stat.value, x + 20, y + 15);
                });
            };

            // Call summary on first page
            addPageOverlay({ doc, pageNumber: 1 }); // Draw header manually for first page background
            drawSummary();

            // 2. Data Table
            const tableColumn = [period === 'week' ? 'DAY' : period === 'month' ? 'WEEK' : 'MONTH', 'HOURS', 'SESSIONS'];
            const tableRows = currentData.data.map(item => [
                'day' in item ? item.day : 'week' in item ? item.week : item.month,
                formatHours(item.hours),
                item.sessions,
            ]);

            autoTable(doc, {
                startY: 82,
                head: [tableColumn],
                body: tableRows,
                theme: 'grid',
                headStyles: {
                    fillColor: brandColor as any,
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'left',
                    fontSize: 9,
                    cellPadding: 4,
                },
                bodyStyles: {
                    textColor: slate900 as any,
                    fontSize: 9,
                    halign: 'left',
                    cellPadding: 4,
                    lineColor: [241, 245, 249],
                },
                columnStyles: {
                    0: { fontStyle: 'bold', cellWidth: 40 },
                    1: { halign: 'center' },
                    2: { halign: 'center' },
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didDrawPage: addPageOverlay,
                margin: { top: 45, bottom: 20 },
            });

            await addWatermarkToPdf(doc);

            const safeDate = dateStr.replace(/[\/\\:]/g, '-');
            doc.save(`Focuzon_Report_${period}_${safeDate}.pdf`);

            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('PDF Generation Error:', error);
            if (onError) onError(error);
        }
    };

    return (
        <button
            onClick={handleDownload}
            disabled={isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0"
        >
            <Download className="w-5 h-5" />
            <span>Download PDF</span>
        </button>
    );
};

export default DownloadPDF;
